/**
 * React Hook for Gallery Settings
 *
 * Manages workspace-level gallery settings (e.g., approval mode).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGallerySettings, updateGallerySettings, type GallerySettings } from '@/lib/api/gallery-v2';

export function useGallerySettings() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['gallerySettings'],
    queryFn: getGallerySettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateMutation = useMutation({
    mutationFn: (settings: Partial<GallerySettings>) => updateGallerySettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallerySettings'] });
    },
  });

  return {
    settings: data,
    isLoading,
    error,
    updateSettings: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
